import { Delete, FetchError, Patch, Post, Put } from '../helpers';
import type { MultipartUploadedPart, MultipartUploadEndpoints } from './types';

/** The API signs at most this many part urls per request, regardless of what we ask for. */
export const MULTIPART_PARTS_PER_REQUEST = 10;

export const GetMultipartEndpoints = async (
  channelId: number,
  filename: string,
  partCount: number,
  contentType = '',
  headers = {}
): Promise<MultipartUploadEndpoints> => {
  return Post({
    path: `/v3/admin/channel/${channelId}/multipart-upload-endpoint`,
    headers,
    body: {
      filename,
      partCount,
      contentType,
    },
  });
};

/**
 * Signs the next batch of part urls for an upload that is already in progress. The key and upload id
 * tie the new urls to the existing object, and the offset tells the API which part numbers to sign.
 * Calling this also resets the API's inactivity timer for the upload.
 */
export const GetMoreMultipartEndpoints = async (
  channelId: number,
  filename: string,
  partCount: number,
  uploadId: string,
  key: string,
  offset: number,
  contentType = '',
  headers = {}
): Promise<MultipartUploadEndpoints> => {
  return Post({
    path: `/v3/admin/channel/${channelId}/multipart-upload-endpoint`,
    headers,
    body: {
      filename,
      partCount,
      contentType,
      uploadId,
      key,
      offset,
    },
  });
};

export const CompleteMultipartUpload = async (
  channelId: number,
  key: string,
  uploadId: string,
  parts: MultipartUploadedPart[]
): Promise<boolean> => {
  return Patch<{ complete: boolean }>({
    path: `/v3/admin/channel/${channelId}/multipart-upload-endpoint`,
    body: {
      key,
      uploadId,
      parts,
    },
  })
    .then((res) => res.complete)
    .catch((e) => {
      console.error(e);
      return false;
    });
};

export const KeepAliveMultipartUpload = async (channelId: number, key: string, uploadId: string): Promise<boolean> => {
  return Put<{ complete: boolean }>({
    path: `/v3/admin/channel/${channelId}/multipart-upload-endpoint`,
    body: {
      key,
      uploadId,
    },
  })
    .then(() => true)
    .catch((e) => {
      console.error(e);
      if (e instanceof FetchError) {
        // upload is gone - user must restart upload.
        if (e.response.status === 410) {
          return false;
        }
      }

      // let error bubble up
      throw e;
    });
};

export const AbortMultipartUpload = async (channelId: number, key: string, uploadId: string): Promise<boolean> => {
  return Delete<{ aborted: boolean }>({
    path: `/v3/admin/channel/${channelId}/multipart-upload-endpoint`,
    body: {
      key,
      uploadId,
    },
  })
    .then((res) => res.aborted)
    .catch((e) => {
      console.error(e);
      return false;
    });
};
